import { captureError } from '@smg-real-estate/browser-rum';
import type { ActionContext } from 'vuex';

import { getUnifiedInsertionById } from '@/api/insertions';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { InsertionStatus, type Insertion, type SourcePlatform } from '@/types';
import type { InsertionsState } from './InsertionsStore';

export type InsertionProcessingGoal = 'publish' | 'archive' | 'reactivate';

export const INSERTION_POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 10000];

export function ensureInsertionDateTypes(insertion: Insertion): void {
  if (insertion.renewalPaymentDueDate) {
    insertion.renewalPaymentDueDate = new Date(insertion.renewalPaymentDueDate);
  }
}

export function isInsertionProcessingComplete(
  status: InsertionStatus,
  goal: InsertionProcessingGoal,
): boolean {
  if (goal === 'archive') {
    return status === InsertionStatus.Archived;
  }

  if (goal === 'reactivate') {
    return status !== InsertionStatus.Republishing;
  }

  if (goal === 'publish') {
    return status === InsertionStatus.Published;
  }

  return false;
}

export function resolveUserIdForInsertion(
  context: ActionContext<InsertionsState, any>,
  insertionId: string,
): string | undefined {
  const insertionFromList = context.state.insertions.find((item) => item.id === insertionId);

  return (
    insertionFromList?.userId ??
    context.rootGetters.getInsertion?.userId ??
    useAgencyStore().activeAgencyPersonId ??
    (useUserStore().logonId || undefined)
  );
}

export function applyInsertionUpdate(
  context: ActionContext<InsertionsState, any>,
  insertion: Insertion,
): void {
  context.commit('upsertInsertion', insertion);

  if (context.rootGetters.getInsertionId === insertion.id) {
    context.commit('setInsertion', insertion);
  }

  if (context.rootGetters.getToplistingInsertionId === insertion.id) {
    context.commit('setToplistingInsertion', insertion);
  }
}

async function refetchInsertionForPoll(
  context: ActionContext<InsertionsState, any>,
  userId: string,
  insertionId: string,
  sourcePlatform: SourcePlatform,
): Promise<InsertionStatus | undefined> {
  try {
    const updatedInsertion = await getUnifiedInsertionById(userId, insertionId, sourcePlatform);
    ensureInsertionDateTypes(updatedInsertion);
    applyInsertionUpdate(context, updatedInsertion);

    return updatedInsertion.status;
  } catch (error) {
    captureError(error);
    return undefined;
  }
}

async function pollInsertionAttempt(
  context: ActionContext<InsertionsState, any>,
  params: {
    insertionId: string;
    userId: string;
    goal: InsertionProcessingGoal;
    sourcePlatform: SourcePlatform;
  },
  delayMs: number,
): Promise<boolean> {
  await new Promise((resolve) => setTimeout(resolve, delayMs));

  const { insertionId, userId, goal, sourcePlatform } = params;
  const status = await refetchInsertionForPoll(context, userId, insertionId, sourcePlatform);

  return status !== undefined && isInsertionProcessingComplete(status, goal);
}

async function runPollAttempts(
  context: ActionContext<InsertionsState, any>,
  params: {
    insertionId: string;
    userId: string;
    goal: InsertionProcessingGoal;
    sourcePlatform: SourcePlatform;
  },
): Promise<void> {
  let index = 0;
  let isComplete = false;

  while (index < INSERTION_POLL_DELAYS_MS.length && !isComplete) {
    isComplete = await pollInsertionAttempt(context, params, INSERTION_POLL_DELAYS_MS[index]);
    index += 1;
  }
}

/**
 * Polls the backend for status updates until the insertion reaches the expected terminal state
 * for the given goal (or the retry budget is exhausted), then calls `onFinish`.
 */
export async function runInsertionProcessingPoll(
  context: ActionContext<InsertionsState, any>,
  params: {
    insertionId: string;
    userId: string;
    goal: InsertionProcessingGoal;
    sourcePlatform: SourcePlatform;
  },
  onFinish: () => void,
): Promise<void> {
  await runPollAttempts(context, params);
  onFinish();
}

export const INSERTION_WILL_BE_DEACTIVATED_TOAST =
  'manageAdsPage.toasts.insertionWillBeDeactivated';

/**
 * Registers an insertion as "processing" for a specific goal and starts polling its status.
 */
export function registerInsertionProcessingWithGoal(
  context: ActionContext<InsertionsState, any>,
  insertionId: string,
  goal: InsertionProcessingGoal,
): void {
  context.commit('registerInsertionProcessing', insertionId);
  context.commit('setInsertionProcessingGoal', { insertionId, goal });
  context.dispatch('pollInsertionProcessingStatus', insertionId).catch((error) => {
    captureError(error);
  });
}

export function applyArchiveOptimisticUpdate(
  context: ActionContext<InsertionsState, any>,
  insertion: Insertion,
  isAgencyUser?: boolean,
): void {
  context.commit('updateInsertionStatus', InsertionStatus.Archiving);
  context.commit('updateCurrentInsertionStatus', {
    insertionId: insertion.id,
    status: InsertionStatus.Archiving,
  });

  if (isAgencyUser) {
    context.commit('setListingActionSuccess', INSERTION_WILL_BE_DEACTIVATED_TOAST);
  } else {
    context.commit('setListingActionSuccessTitle', INSERTION_WILL_BE_DEACTIVATED_TOAST);
    context.commit('setListingActionSuccess', 'manageAdsPage.toasts.confirmationEmailNotice');
  }
}
